import { createSlice } from '@reduxjs/toolkit';
import { URL } from '../../utils/constants';
import { OP_VALUES_CATEGORIES, OP_VALUES } from '../../components/OperatingValues/constants';
import { CHARTS } from '../../components/Charts/constants';
import { BOM_ATTRIBUTES, BOM_COMPONENTS } from '../../components/BomTable/constants';
import userSlice from './userSlice';
import { getDomain } from '../../utils/config';

const domain = getDomain();

const selectedDesignSlice = createSlice({
	name: 'selectedDesign',
	slice: 'selectedDesign',
	initialState: {
		openMyDesign: false,
		fetchDesignData: true,
		icPartNumber: '',
		topology: '',
		initialInputParameters: {}, // Before calculation, entered by user
		detail: {
			inputParameters: {},
			opValueCategories: [],
			bom: [],
			charts: [],
			rankingParameters: {
				datasheetUrl: URL.ONSEMI,
				productPageUrl: URL.ONSEMI
			}
		},
		myDesign: {
			id: 0,
			name: '',
			dateCreated: '',
			dateModified: ''
		}
	},
	reducers: {
		setOpenMyDesign(state, action) {
			state.openMyDesign = action.payload;
		},
		setFetchDesignData(state, action) {
			if (action.payload) {
				state.detail = {
					inputParameters: {} /* sync with inputParameter above, or delete */,
					opValueCategories: [],
					bom: [],
					charts: [],
					rankingParameters: {
						datasheetUrl: URL.ONSEMI,
						productPageUrl: URL.ONSEMI
					}
				};
				state.myDesign = {
					id: 0,
					name: '',
					dateCreated: '',
					dateModified: ''
				};
			}

			state.fetchDesignData = action.payload;
		},
		setIcPartNumber(state, action) {
			state.icPartNumber = action.payload;
		},
		setTopology(state, action) {
			state.topology = action.payload;
		},
		setInitialInputParameters(state, action) {
			const inputParameters = {};

			Object.keys(action.payload).forEach(ipKey => {
				const newValue = action.payload[ipKey];
				inputParameters[ipKey] = newValue === null ? '' : newValue;
			});

			state.initialInputParameters = inputParameters;
		},
		setMyDesignId(state, action) {
			state.myDesign.id = action.payload;
		},
		setMyDesignName(state, action) {
			state.myDesign.name = action.payload;
		},
		setMyDesignData(state, action) {
			state.myDesign.id = action.payload.id;
			state.myDesign.name = action.payload.name;
			state.myDesign.dateCreated = action.payload.dateCreated;
			state.myDesign.dateModified = action.payload.dateModified;
		},
		setDetail(state, action) {
			state.detail = action.payload;
			/* Keep 0 only on x axis for Efficiency vs. Iout chart */
			replaceDomain(state.detail);
			if (state.detail.charts.some(chart => chart.chartName === 'EfficiencyVsIout')) {
				state.detail.charts
					.find(chart => chart.chartName === 'EfficiencyVsIout')
					.series.forEach(series => {
						series.items[0] = { x: 0, y: null };
					});
			}

			/* Input parameters formatting */
			const formattedInputParameters = {};
			const rawInputParameters = action.payload.inputParameters;

			Object.keys(rawInputParameters).forEach(ipKey => {
				const newValue = rawInputParameters[ipKey];
				formattedInputParameters[ipKey] =
					newValue === null ? '' : ipKey === 'vinNom' ? parseFloat(newValue.toFixed(2)) : newValue;
			});

			state.detail.inputParameters = formattedInputParameters;

			/* Operating values formatting */
			state.detail.opValueCategories = state.detail.opValueCategories.sort((a, b) =>
				!OP_VALUES_CATEGORIES[a.name]
					? -1
					: !OP_VALUES_CATEGORIES[b.name] || OP_VALUES_CATEGORIES[a.name].index > OP_VALUES_CATEGORIES[b.name].index
					? 1
					: -1
			);

			const formattedOpValueCategories = [];
			let tempOpValues;
			state.detail.opValueCategories.forEach(opvCategory => {
				opvCategory.operatingValues = opvCategory.operatingValues.sort((a, b) =>
					!OP_VALUES[a.name] ? -1 : !OP_VALUES[b.name] || OP_VALUES[a.name].index > OP_VALUES[b.name].index ? 1 : -1
				);

				/* If op. value exists in constants: merge value & unit, assign description based on value if possible */
				tempOpValues = [];
				opvCategory.operatingValues.forEach(opv => {
					tempOpValues.push(
						OP_VALUES[opv.name]
							? {
									name: opv.name,
									label: OP_VALUES[opv.name].label,
									value: opv.value.unit === null ? opv.value.value : opv.value.value + ' ' + opv.value.unit,
									description: OP_VALUES[opv.name].description.hasOwnProperty(opv.value.value)
										? OP_VALUES[opv.name].description[opv.value.value]
										: OP_VALUES[opv.name].description
							  }
							: {
									name: opv.name,
									label: opv.name,
									value: opv.value.value + ' ' + opv.value.unit,
									description: ''
							  }
					);
				});

				formattedOpValueCategories.push({
					name: opvCategory.name,
					operatingValues: tempOpValues
				});
			});

			// Merge all operating values to one 'All' property
			let opvIndex;
			const allOpValues = [];
			formattedOpValueCategories.forEach(opvCategory => {
				opvCategory.operatingValues.forEach(opValue => {
					opvIndex = allOpValues.findIndex(o => o.label === opValue.label);

					if (opvIndex === -1) {
						allOpValues.push(opValue);
					}
				});
			});
			formattedOpValueCategories.unshift({
				name: 'ALL',
				operatingValues: allOpValues
			});

			state.detail.opValueCategories = formattedOpValueCategories;

			/* Charts sorting */
			state.detail.charts = state.detail.charts.sort((a, b) =>
				!CHARTS[a.chartName]
					? -1
					: !CHARTS[b.chartName] || CHARTS[a.chartName].index > CHARTS[b.chartName].index
					? 1
					: -1
			);

			/* BOM sorting */
			state.detail.bom = state.detail.bom.sort((a, b) =>
				!BOM_COMPONENTS[a.part]
					? -1
					: !BOM_COMPONENTS[b.part] || BOM_COMPONENTS[a.part].index > BOM_COMPONENTS[b.part].index
					? 1
					: -1
			);

			/* BOM formatting */
			let label;
			state.detail.bom.forEach(item => {
				item.attributes.forEach(attribute => {
					label = BOM_ATTRIBUTES[attribute.name] ? BOM_ATTRIBUTES[attribute.name] : attribute.name;
					attribute.formatted =
						attribute.value.unit === null
							? label + ' = ' + attribute.value.value
							: label + ' = ' + attribute.value.value + ' ' + attribute.value.unit;
					attribute.value.label = label;
				});
			});

			/* BOM label formating */
			state.detail.bom.forEach(item => {
				item.part = BOM_COMPONENTS[item.part]?.label ? BOM_COMPONENTS[item.part].label : item.part;
			});
		},
		setSchematicSvg(state, action) {
			state.schematicSvg = action.payload;
		}
	},
	extraReducers: {
		[userSlice.actions.logOut]() {
			window.location.reload();
		},
		[userSlice.actions.updateMyDesignName](state, action) {
			state.myDesign.name = action.payload.name;
		}
	}
});

const replaceDomain = detail => {
	if (domain === 'onsemi.cn') {
		const bom = detail.bom;
		bom.forEach(item => {
			const datasheetLink = item.datasheetLink;
			if (datasheetLink && datasheetLink.length > 0 && datasheetLink.includes('onsemi.com')) {
				item.datasheetLink = datasheetLink.replace('onsemi.com', 'onsemi.cn');
			}
		});
		if (detail.rankingParameters) {
			const datasheetUrl = detail.rankingParameters.datasheetUrl;
			if (datasheetUrl && datasheetUrl.length > 0 && datasheetUrl.includes('onsemi.com')) {
				detail.rankingParameters.datasheetUrl = datasheetUrl.replace('onsemi.com', 'onsemi.cn');
			}
			const productPageUrl = detail.rankingParameters.productPageUrl;
			if (productPageUrl && productPageUrl.length > 0 && productPageUrl.includes('onsemi.com')) {
				detail.rankingParameters.productPageUrl = productPageUrl.replace('onsemi.com', 'onsemi.cn');
			}
		}
	}
};

export default selectedDesignSlice;
